import type { TFunction } from 'i18next';
import { getJSON, type WalletSessionResponse, type WalletsResponse, userWalletApiBase } from './api';
import { decryptBytesWithPaymentPassword, encryptBytesWithPaymentPassword } from './paymentCrypto';

export type WalletRotateBlob = {
  wallet_id: number;
  encrypted_client_session: string;
  encrypted_client_secret: string;
};

/** For each wallet: decrypt blobs with old payment password and encrypt with new one (stub wallets pass ciphertext through). */
export async function buildPaymentPasswordRotatePayload(
  oldPwd: string,
  newPwd: string,
  t: TFunction,
): Promise<WalletRotateBlob[]> {
  const { res, data } = await getJSON<WalletsResponse>('/api/user/wallets');
  if (!res.ok) {
    throw new Error(data.error || t('common.error'));
  }
  const list = data.wallets ?? [];
  const out: WalletRotateBlob[] = [];
  for (const w of list) {
    const { res: sr, data: sess } = await getJSON<WalletSessionResponse>(`${userWalletApiBase(w.id)}/session`);
    if (!sr.ok || !sess.encrypted_client_session?.trim()) {
      throw new Error(sess.error || t('settings.payReencryptSessionFailed'));
    }
    const encSess = sess.encrypted_client_session.trim();
    const encSecRaw = sess.encrypted_client_secret?.trim() ?? '';
    if (w.source === 'stub') {
      out.push({
        wallet_id: w.id,
        encrypted_client_session: encSess,
        encrypted_client_secret: encSecRaw || encSess,
      });
      continue;
    }
    if (!encSecRaw) {
      throw new Error(t('settings.payReencryptMissingSecret'));
    }
    let plainSess: Uint8Array;
    let plainSec: Uint8Array;
    try {
      plainSess = await decryptBytesWithPaymentPassword(oldPwd, encSess);
      plainSec = await decryptBytesWithPaymentPassword(oldPwd, encSecRaw);
    } catch {
      throw new Error(t('settings.payReencryptFail'));
    }
    const nextSess = await encryptBytesWithPaymentPassword(newPwd, plainSess);
    const nextSec = await encryptBytesWithPaymentPassword(newPwd, plainSec);
    out.push({
      wallet_id: w.id,
      encrypted_client_session: nextSess,
      encrypted_client_secret: nextSec,
    });
  }
  return out;
}
